import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

const RiderContext = createContext();

const RiderEvents = new EventEmitter();

export const RiderProvider = ({ children }) => {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [searchParams, setSearchParams] = useState({
    search: "",
    partnerId: "",
  });

  // Update pagination state
  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  // Update search parameters
  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const createRider = async (data) => {
    try {
      const response = await axios.post(`/riders/`, data);
      const rider = response.data;

      setRiders((prevData) => [rider, ...prevData]);

      RiderEvents.emit("rider:created");

      return rider;
    } catch (error) {
      console.error("Error creating rider:", error);
      throw error;
    }
  };

  const getRiders = async () => {
    try {
      setLoading(true);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (searchParams.search) params.search = searchParams.search;
      if (searchParams.partnerId) params.partnerId = searchParams.partnerId;

      const response = await axios.get(`/riders`, { params });
      const { data, total, totalPages } = response.data;

      setRiders(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching riders:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getRiderById = async (riderId) => {
    try {
      const response = await axios.get(`/riders/${riderId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching rider by ID:", error);
      throw error;
    }
  };

  const updateRider = async (riderId, updatedData) => {
    try {
      const response = await axios.put(`/riders/${riderId}`, updatedData);
      const updatedRider = response.data;

      setRiders((prevData) =>
        prevData.map((data) =>
          data.id === updatedRider.id ? updatedRider : data
        )
      );

      RiderEvents.emit("rider:updated");

      return updatedRider;
    } catch (error) {
      console.error("Error updating rider:", error);
      throw error;
    }
  };

  const setRiderCities = async (riderId, cityIds) => {
    try {
      const response = await axios.put(`/riders/${riderId}/cities`, {
        cityIds,
      });
      const updatedRider = response.data;
      setRiders((prevData) =>
        prevData.map((data) =>
          data.id === updatedRider.id ? updatedRider : data
        )
      );
      RiderEvents.emit("rider:updated");
      return updatedRider;
    } catch (error) {
      console.error("Error setting rider cities:", error);
      throw error;
    }
  };

  const activateRider = async (riderId) => {
    try {
      await axios.put(`/riders/${riderId}/activate`);
      setRiders((prevData) =>
        prevData.map((r) =>
          r.id === riderId ? { ...r, status: "active" } : r
        )
      );
      RiderEvents.emit("rider:updated");
    } catch (error) {
      console.error("Error activating rider:", error);
      throw error;
    }
  };

  const deactivateRider = async (riderId) => {
    try {
      await axios.put(`/riders/${riderId}/deactivate`);
      setRiders((prevData) =>
        prevData.map((r) =>
          r.id === riderId ? { ...r, status: "deactivated" } : r
        )
      );
      RiderEvents.emit("rider:updated");
    } catch (error) {
      console.error("Error deactivating rider:", error);
      throw error;
    }
  };

  const suspendRider = async (riderId) => {
    try {
      await axios.put(`/riders/${riderId}/suspend`);
      setRiders((prevData) =>
        prevData.map((r) =>
          r.id === riderId ? { ...r, status: "suspended" } : r
        )
      );
      RiderEvents.emit("rider:updated");
    } catch (error) {
      console.error("Error suspending rider:", error);
      throw error;
    }
  };

  const disconnectRider = async (riderId) => {
    try {
      await axios.post(`/riders/${riderId}/disconnect`);
    } catch (error) {
      console.error("Error disconnecting rider:", error);
      throw error;
    }
  };

  const resetRiderPassword = async (riderId) => {
    try {
      const response = await axios.put(`/riders/${riderId}/password/reset`);
      return response.data;
    } catch (error) {
      console.error("Error resetting rider password:", error);
      throw error;
    }
  };

  const deleteRider = async (riderId) => {
    try {
      await axios.delete(`/riders/${riderId}`);

      setRiders((prevData) => prevData.filter((data) => data.id !== riderId));

      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));

      RiderEvents.emit("rider:updated");
    } catch (error) {
      console.error("Error deleting rider:", error);
      throw error;
    }
  };

  // Context value
  const value = {
    riders,
    loading,
    pagination,
    searchParams,
    createRider,
    getRiders,
    getRiderById,
    updateRider,
    setRiderCities,
    activateRider,
    deactivateRider,
    suspendRider,
    disconnectRider,
    resetRiderPassword,
    deleteRider,
    updatePagination,
    updateSearchParams,
    RiderEvents,
  };

  return (
    <RiderContext.Provider value={value}>{children}</RiderContext.Provider>
  );
};

// Custom hook to use RiderContext
export const useRiderContext = () => {
  const context = useContext(RiderContext);
  if (!context) {
    throw new Error("useRiderContext must be used within a RiderProvider");
  }
  return context;
};
